/**
 * @file DocumentCard.tsx
 * @description Карточка строки документа или реестра на mobile
 * (phase19.24, UI-контракт §0.2 и §2.1).
 *
 * До контракта строку на 360px каждый экран верстал заново — 20 ручных
 * карточек с разными отступами, бордерами и порядком полей. Здесь порядок
 * фиксирован: слева медиа (фото фасовки), в шапке название и количество,
 * под ними код (моно) и строки метаданных, снизу действия.
 *
 * Поверхность карточки непрозрачная в любом тоне (SC69, phase19.31).
 *
 * Состояние показываем фоном и ring, а не бордером (No-Line Rule):
 * `selected` — только что отсканировали, `done` — строка закрыта,
 * `danger` — недостача или ошибка.
 *
 * ```tsx
 * <DocumentCard
 *   media={<img src={photo} alt="" className="size-14 rounded-lg object-cover" />}
 *   title="Иван-чай гранулированный"
 *   code="IVC-050"
 *   meta={["50 г · L-240517 · до 17.05.27", "Ячейка A-03-02"]}
 *   qty={<span className="tabular-nums">2 / 3 шт</span>}
 *   actions={<QtyStepper … />}
 *   tone="selected"
 * />
 * ```
 */

import type { ReactNode } from "react";

import { LIST_SURFACE_CLASS, ROW_SURFACE_TINT } from "./ListStates";
import { cn } from "@/lib/utils";

/** Состояние строки: спокойная, отмеченная, закрытая, проблемная. */
export type DocumentCardTone = "default" | "selected" | "done" | "danger";

/**
 * Подложки тонов — непрозрачные (SC69): полупрозрачная `bg-accent/50`
 * перебивала белую поверхность и пропускала декоративный фон кабинета.
 */
const TONE_CLASS: Record<DocumentCardTone, string> = {
  default: "ring-ghost",
  selected: cn(ROW_SURFACE_TINT.selected, "shadow-[inset_0_0_0_2px_var(--primary)]"),
  done: "shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--primary)_35%,transparent)]",
  danger: cn(ROW_SURFACE_TINT.danger, "shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--destructive)_40%,transparent)]"),
};

interface Props {
  /** Фото фасовки или иконка-заглушка 56px — левая колонка карточки. */
  media?: ReactNode;
  /** Название позиции или номер документа — главная строка. */
  title: ReactNode;
  /** Артикул, номер партии, код ячейки — моноширинным. */
  code?: ReactNode;
  /** Вторичные строки: фасовка, партия, склад, получение. Пустые отбрасываем. */
  meta?: ReactNode[];
  /** Количество справа от названия: «2 / 3 шт», «12 шт». */
  qty?: ReactNode;
  /** Нижний ряд: степпер, кнопки строки. Тач-цель ≥56px делает сам слот. */
  actions?: ReactNode;
  tone?: DocumentCardTone;
  className?: string;
}

export function DocumentCard({
  media,
  title,
  code,
  meta,
  qty,
  actions,
  tone = "default",
  className,
}: Props) {
  const metaRows = (meta ?? []).filter(Boolean);
  return (
    <article
      data-tone={tone}
      className={cn(
        "flex flex-col gap-3 rounded-xl p-4 transition-colors",
        LIST_SURFACE_CLASS,
        TONE_CLASS[tone],
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        {media ? <div className="shrink-0">{media}</div> : null}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 items-start justify-between gap-2">
            {/* line-clamp-2: длинное название не растягивает карточку в столбик. */}
            <span className="min-w-0 line-clamp-2 text-base font-medium">{title}</span>
            {qty ? (
              <span className="shrink-0 text-base font-medium tabular-nums">{qty}</span>
            ) : null}
          </div>
          {code ? (
            <span className="font-mono text-sm text-on-surface-variant">{code}</span>
          ) : null}
          {metaRows.map((row, index) => (
            <span
              // Строки метаданных — статичный список без своих id, порядок задаёт вызывающий.
              key={index}
              className="text-sm text-on-surface-variant"
            >
              {row}
            </span>
          ))}
        </div>
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </article>
  );
}
